import { Injectable } from '@angular/core';

import { DefaultFunnel } from 'src/app/models/defaultFunnel';
import { PostPurchaseProduct } from 'src/app/models/postPurchaseProduct';
import { ProductOption } from 'src/app/models/productOption';
import { UpsellProduct } from 'src/app/models/upsellProduct';
import { InfoUpsellProduct } from 'src/app/models/infoUpsellProduct';
import { Units } from 'src/app/models/units';
import { Names } from 'src/app/models/names';
import { DisplayProduct } from 'src/app/models/displayProduct';

/** Result of grouping a list by a repeated key. */
export interface RepeatedFunnels<T> {
  repeated: T[];
  repeatedValues: string[];
}

/** Per-option image URLs, indexed 1..4 to match the funnel option slots. */
export interface OptionImages {
  imageOptionProduct1: string;
  imageOptionProduct2: string;
  imageOptionProduct3: string;
  imageOptionProduct4: string;
}

/** The two quantity values used when building product options. */
export interface ProductQuantities {
  productQuantity: number;
  productQuantityPack: number;
}

/**
 * Stateless factory for assembling multi-upsell products from funnel config.
 *
 * Extracted verbatim from USGenericComponent. Holds NO state: every method takes
 * its inputs as parameters and returns results. Behavior is preserved exactly,
 * including the `orderOptions` do/while loop and the `getActualProductInfo`
 * `upsellPage ? 'upsell' : 'downsell'` quirk — both are pinned by characterization
 * tests and must not be "fixed".
 */
@Injectable({ providedIn: 'root' })
export class MultiUpsellProductFactory {
  getMultiUpsellFunnels<T>(arr: T[], key: keyof T): RepeatedFunnels<T> {
    const countMap = arr.reduce((acc, item) => {
      const value = String(item[key]);
      acc[value] = (acc[value] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);
    const repeatedValues = Object.keys(countMap).filter(value => countMap[value] > 1);
    const repeated = arr.filter(item => countMap[String(item[key])] > 1);

    return { repeated, repeatedValues };
  }

  orderOptions(optionsList: DefaultFunnel[], optionsNames: string[]): DefaultFunnel[] {
    let displayedArray = [];
    let index = 0;

    do {
      const res = optionsList.filter((obj: DefaultFunnel) => {
        // Non-null assertion (not `?.`) to preserve the original verbatim behavior:
        // a row without `upsell` throws a TypeError here, exactly as the pre-extraction
        // code did. Using `?.` would silently swallow that throw — a behavior change.
        return obj.upsell!.name.includes(optionsNames[index]);
      });

      displayedArray.push(res[0]);

      index++;

    }
    while (optionsList.length !== displayedArray.length)

    return displayedArray;
  }

  getActualProductInfo(funnels: DefaultFunnel[], pageFunnelConfig: number): PostPurchaseProduct {
    const upsellPage = funnels[pageFunnelConfig].upsell;

    // `upsell` / `downsell` are optional on DefaultFunnel, so the indexed access is
    // `PostPurchaseProduct | undefined`. The original component method was typed `any`;
    // the locked return type is `PostPurchaseProduct`. For well-formed funnel config the
    // selected branch is always present, so a non-null assertion preserves runtime behavior.
    return funnels[pageFunnelConfig][upsellPage ? 'upsell' : 'downsell']!;
  }

  createProductOption(
    productInfo: PostPurchaseProduct,
    quantity: number,
    unit: string,
    secondName: string,
    image: string,
    displayName: string
  ): ProductOption {
    return {
      quantity,
      unit,
      regularPrice: productInfo.standardPrice,
      offerPrice: productInfo.offerPrice,
      sfid: productInfo.sfid,
      name: productInfo.name,
      shippingCost: productInfo.shippingCost || null,
      isSubscription: productInfo.isSubscriptionAvailable,
      secondName: secondName,
      img: image,
      displayName: displayName,
      standardPrice: productInfo.standardPrice,
    };
  }

  createProductOptions(
    funnels: DefaultFunnel[],
    productPage: InfoUpsellProduct['productPage'],
    quantities: ProductQuantities,
    units: Units,
    productNames: Names,
    images: OptionImages,
    displayName: DisplayProduct
  ): ProductOption[] {
    const productOption1 = this.createProductOption(
      this.getActualProductInfo(funnels, productPage.product1),
      quantities.productQuantity,
      units.quantityOptionProduct1,
      productNames.nameOptionProduct1,
      images.imageOptionProduct1,
      displayName.displayProductProduct1
    );

    const productOption2 = this.createProductOption(
      this.getActualProductInfo(funnels, productPage.product2),
      quantities.productQuantity,
      units.quantityOptionProduct2,
      productNames.nameOptionProduct2,
      images.imageOptionProduct2,
      displayName.displayProductProduct2
    );

    const productOption3 = this.createProductOption(
      this.getActualProductInfo(funnels, productPage.product3),
      quantities.productQuantity,
      units.quantityOptionProduct3,
      productNames.nameOptionProduct3,
      images.imageOptionProduct3,
      displayName.displayProductProduct3
    );

    const productOption4 = this.createProductOption(
      this.getActualProductInfo(funnels, productPage.product4),
      quantities.productQuantityPack,
      units.quantityOptionProduct4,
      productNames.nameOptionProduct4,
      images.imageOptionProduct4,
      displayName.displayProductProduct4
    );

    return [productOption1, productOption2, productOption3, productOption4];
  }

  createUpsellProduct(
    funnels: DefaultFunnel[],
    infoUpsellProduct: InfoUpsellProduct,
    quantities: ProductQuantities
  ): UpsellProduct {
    const { funnelGroupName, units, productPage, productNames, images, displayName } = infoUpsellProduct;
    return {
      funnelGroupName,
      imageUrl: this.getActualProductInfo(funnels, productPage.product1).product.image[0].imageUrl,
      productOptions: this.createProductOptions(
        funnels,
        productPage,
        quantities,
        units,
        productNames,
        images,
        displayName
      ),
    };
  }
}
